import {
  ColumnFiltersState,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import { type ClassValue, clsx } from 'clsx'
import { saveAs } from 'file-saver'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { ListResult } from 'pocketbase'
import { twMerge } from 'tailwind-merge'
import {
  UsersRolesOptions,
  TrackingStatesAllowedRolesOptions,
} from '@/utils/types/pocketbase-types'
import { CommandSearchParams } from '@/components/layout/types'
import {
  RECORD_TYPE as COMMAND_RECORD_TYPE,
  RECORD_TYPE,
} from '@/features/commands/data/data'
import { RECORD_TYPE as FACTURE_RECORD_TYPE_CLIENT } from '@/features/factures-clients/data/data'
import { RECORD_TYPE as FACTURE_RECORD_TYPE } from '@/features/factures-livreurs/data/data'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export type UsersRolesOptionsStrings = `${UsersRolesOptions}`
export const UsersRolesOptionsArray: string[] = Object.values(UsersRolesOptions)

export type TrackingStatesAllowedRolesOptionsStrings =
  `${TrackingStatesAllowedRolesOptions}`
export const TrackingStatesAllowedRolesOptionsArray: string[] = Object.values(
  TrackingStatesAllowedRolesOptions
)

export function formatSortItems(items: SortingState): string {
  return items.map((item) => (item.desc ? `-${item.id}` : item.id)).join(',')
}

// Helper function to format a single filter condition
function formatSingleFilter(id: unknown, value: unknown): string {
  if (typeof value === 'boolean') {
    return `${id} = ${value}`
  }

  if (Array.isArray(value)) {
    if (
      value.some(
        (v) =>
          typeof v === 'object' &&
          v !== null &&
          'value' in v &&
          'type' in v &&
          v.type === 'exact'
      )
    ) {
      const conditions = value.map((v) => `${id} = "${v.value}"`).join(' || ')
      return value.length > 1 ? `(${conditions})` : conditions
    } else {
      return value
        .map(
          (v) =>
            `${id} ~ "${
              typeof v === 'object' && v !== null && 'value' in v ? v.value : v
            }"`
        )
        .join(' || ')
    }
  }

  if (
    typeof value === 'object' &&
    value !== null &&
    ('from' in value || 'to' in value)
  ) {
    const hasFrom = 'from' in value && value.from
    const hasTo = 'to' in value && value.to

    if (hasFrom && hasTo) {
      const from = new Date(value.from as string | number | Date)
        .toISOString()
        .replace('T', ' ')
      const to = new Date(value.to as string | number | Date)
        .toISOString()
        .replace('T', ' ')
      return `${id} >= "${from}" && ${id} <= "${to}"`
    } else if (hasFrom) {
      const from = new Date(value.from as string | number | Date)
        .toISOString()
        .replace('T', ' ')
      return `${id} >= "${from}"`
    } else if (hasTo) {
      const to = new Date(value.to as string | number | Date)
        .toISOString()
        .replace('T', ' ')
      return `${id} <= "${to}"`
    }
  }

  if (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    'type' in value &&
    value.type === 'exact'
  ) {
    return `${id} = "${value.value}"`
  }

  if (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    'type' in value &&
    value.type === 'not-equal'
  ) {
    return `${id} != "${value.value}"`
  }

  if (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    'type' in value &&
    value.type === 'contains'
  ) {
    return `${id} ~ "${value.value}"`
  }

  if (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    'type' in value &&
    value.type === 'not-null'
  ) {
    return `${id} != null`
  }

  return `${id} ~ "${
    typeof value === 'object' && value !== null && 'value' in value
      ? value.value
      : value
  }"`
}

export function formatORSearchQuery(filters: ColumnFiltersState): string {
  const orQuery = filters
    .map((filter) => formatSingleFilter(filter.id, filter.value))
    .filter(Boolean)
    .join(' || ')

  return filters.length > 1 ? `(${orQuery})` : orQuery
}

export function formatANDSearchQuery(filters: ColumnFiltersState): string {
  return filters
    .map((filter) => {
      const { id, value } = filter

      // Handle OR groups - special case where value is an object with type: 'orGroup'
      if (
        typeof value === 'object' &&
        value !== null &&
        'type' in value &&
        value.type === 'orGroup' &&
        'filters' in value &&
        Array.isArray(value.filters)
      ) {
        const orConditions = value.filters
          .map((f) => {
            // Handle AND groups within OR groups
            if (
              typeof f === 'object' &&
              f !== null &&
              'type' in f &&
              f.type === 'andGroup' &&
              'filters' in f &&
              Array.isArray(f.filters)
            ) {
              const andConditions = f.filters
                .map((af: any) => formatSingleFilter(af.id, af.value))
                .filter(Boolean)
                .join(' && ')
              return `(${andConditions})`
            }
            return formatSingleFilter(f.id, f.value)
          })
          .filter(Boolean)
          .join(' || ')
        return `(${orConditions})`
      }

      if (typeof value === 'boolean') {
        return `${id} = ${value}`
      }

      if (Array.isArray(value)) {
        let conditions: string
        if (
          value.some(
            (v) =>
              typeof v === 'object' &&
              v !== null &&
              'value' in v &&
              'type' in v &&
              v.type === 'exact'
          )
        ) {
          conditions = value.map((v) => `${id} = "${v.value}"`).join(' || ')
        } else if (
          value.some(
            (v) =>
              typeof v === 'object' &&
              v !== null &&
              'value' in v &&
              'type' in v &&
              v.type === 'not-equal'
          )
        ) {
          conditions = value.map((v) => `${id} != "${v.value}"`).join(' && ')
        } else if (
          value.some(
            (v) =>
              typeof v === 'object' &&
              v !== null &&
              'value' in v &&
              'type' in v &&
              v.type === 'contains'
          )
        ) {
          conditions = value.map((v) => `${id} ~ "${v.value}"`).join(' || ')
        } else {
          conditions = value.map((v) => `${id} ~ "${v}"`).join(' || ')
        }
        // Wrap OR conditions in parentheses when there are multiple OR conditions
        return value.length > 1 ? `(${conditions})` : conditions
      }

      return formatSingleFilter(id, value)
    })
    .join(' && ')
}

//  create a function that return a random uuid of this kind : BL-29052025-132557 ,Bl are constant
export function generateBonLivraisonId(): string {
  const date = new Date()
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
  const year = date.getFullYear().toString().slice(-2) // Last two digits of the year
  const time = date.toTimeString().split(' ')[0].replace(/:/g, '') // HHMMSS format

  return `${day}${month}${year}-${time}`
}

export const convertToCSV = (data: COMMAND_RECORD_TYPE[]) => {
  const rowsObject = data.map((row) => {
    return {
      DESTINATAIRE: row.receiver_full_name,
      ADRESSE: row.receiver_address,
      TELEPHONE: row.receiver_phone_number,
      VILLE: row.expand?.city?.name ?? '',
      MARCHANDISE: row.product_name,
      Quantité: row.quantity,
      PRIX: row.price,
      'FRAIS SUPPLEMENTAIRES': row.extra_fees,
      TOTAL: row.extra_fees + row.price,
      'CODE SUIVI': row.code,
      COMMENTAIRE: row.note,
      "DATE D'ENVOI": new Date(row.created).toLocaleDateString('fr-FR'),
      LIVREUR: row.expand?.delivery_man?.full_name ?? '',
      CLIENT: row.receiver_full_name ?? '',
      'DATE DE RAPPORT': row.reported_date
        ? new Date(row.reported_date).toLocaleDateString('fr-FR')
        : '',
      ETAT: row.expand?.status?.name,
      'VENDEUR NOM': row.client_name,
      'VENDEUR TELEPHONE': row.client_phone,
      'COLIS REMPLACER': row.is_echange ? 'OUI' : 'NON',
      'PEUT OUVRIR': row.package_opened ? 'OUI' : 'NON',
    }
  })

  if (rowsObject.length === 0) return ''

  const csvRows = [
    Object.keys(rowsObject[0])
      .map((key) => `"${key}"`)
      .join(';'),
    ...rowsObject.map((row) =>
      Object.values(row)
        .map((value) => `"${value}"`)
        .join(';')
    ),
  ]
  const csvStringWithBom = '\uFEFF' + csvRows.join('\n')
  const blob = new Blob([csvStringWithBom], { type: 'text/csv;charset=utf-8;' })
  saveAs(URL.createObjectURL(blob), `${new Date().toDateString()}.csv`)
}

export const calculateFactureMontantLivreurVersion = (
  fctr: FACTURE_RECORD_TYPE
): number => {
  return (
    fctr.expand?.factures_details_via_facture?.reduce((prev, curr) => {
      return prev + curr.price
    }, 0) ?? 0
  )
}

export const calculateFactureMontantClientVersion = (
  fctr: FACTURE_RECORD_TYPE_CLIENT
): number => {
  return (
    fctr.expand?.factures_details_via_facture?.reduce((prev, curr) => {
      return prev + curr.price
    }, 0) ?? 0
  )
}

export type CommandQueryExtension = {
  title: string
  querykey: string
  initialFilter?: ColumnFiltersState
  queryFn: (data: {
    page?: number
    perPage?: number
    filter?: string
    sort?: string
  }) => Promise<ListResult<RECORD_TYPE>>
}

type Primitive = string | number | boolean | null | undefined

type Diff<T, U> = {
  [K in keyof U as K extends keyof T
    ? Equal<T[K], U[K]> extends true
      ? never
      : K
    : K]: U[K] extends Primitive
    ? U[K]
    : U[K] extends object
      ? K extends keyof T
        ? T[K] extends object
          ? Diff<T[K], U[K]>
          : U[K]
        : U[K]
      : never
}

// Utility to check deep equality of types
type Equal<X, Y> =
  (<T>() => T extends X ? 1 : 2) extends <T>() => T extends Y ? 1 : 2
    ? true
    : false

export function diffObjects<T extends object, U extends object>(
  obj1: T,
  obj2: U
): Diff<T, U> {
  const result = {} as Diff<T, U>

  for (const key in obj2) {
    if (!Object.prototype.hasOwnProperty.call(obj2, key)) continue

    const val2 = obj2[key]
    const val1 = (obj1 as Record<string, unknown>)[key]

    if (!(key in obj1)) {
      ;(result as Record<string, unknown>)[key] = val2
    } else if (isObject(val1) && isObject(val2)) {
      const nested = diffObjects(val1, val2)
      if (Object.keys(nested).length > 0) {
        ;(result as Record<string, unknown>)[key] = nested
      }
    } else if (val1 !== val2) {
      ;(result as Record<string, unknown>)[key] = val2
    }
  }

  return result
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
export function normalizeMoroccanPhone(input: string) {
  const phone = parsePhoneNumberFromString(input, 'MA') // 'MA' = Morocco
  if (phone && phone.isValid()) {
    return phone.number
    // or return phone.number // e.g., "+212612345678"
  }
  return null
}
export function nextThreshold(n: number) {
  const thresholds = [20, 50, 100, 250, 500]
  return thresholds.find((t) => t >= n) ?? 250
}

const APP_VERSION = '0.0.9'
const APP_NAME = import.meta.env.VITE_APP_NAME || 'application Livraison'

export { APP_VERSION, APP_NAME }

export const validateCommandsPageSearchParams = (
  search: Record<string, unknown>
): CommandSearchParams => {
  return {
    filter: (search.filter as ColumnFiltersState) || undefined,
    sorting: (search.sorting as SortingState) || undefined,
    pagination: search.pagination as PaginationState,
  }
}

export function detectFileType(
  filename: string
): 'image' | 'video' | 'document' | 'unknown' {
  const ext = filename.split('.').pop()?.toLowerCase() || ''

  const imageExtensions = new Set([
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
    'bmp',
    'tiff',
    'tif',
    'svg',
    'ico',
    'heic',
    'heif',
    'avif',
  ])

  const videoExtensions = new Set([
    'mp4',
    'mov',
    'avi',
    'mkv',
    'wmv',
    'flv',
    'webm',
    'mpg',
    'mpeg',
    '3gp',
    'm4v',
  ])

  const documentExtensions = new Set([
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'txt',
    'rtf',
    'md',
    'csv',
    'json',
    'xml',
    'html',
    'odt',
    'ods',
    'odp',
  ])

  if (imageExtensions.has(ext)) return 'image'
  if (videoExtensions.has(ext)) return 'video'
  if (documentExtensions.has(ext)) return 'document'

  return 'unknown'
}

export const getLocalName = (language: string) => {
  if (language === 'fr') {
    return 'fr-FR'
  } else if (language === 'ar' || language === 'darija') {
    return 'ar-MA'
  }
}
